import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Notification} from '@Components/notification';
import {isUserPremium} from '@Libraries/user.library';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {formatNumber, formatToNearestThousand} from '@Utils/number.util';
import {useGetRemoveImageBackgroundCreditsCost} from '@Hooks/useGetRemoveImageBackgroundCreditsCost';
import {Tooltip, TOOLTIP_POSITION} from '@Components/tooltip';
import {Text, TextSize, TextType} from '@Components/text';
import styles from './remove-image-background-notification-tag.module.scss';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';

interface RemoveImageBackgroundNotificationTagProps {
  hashedFilename?: string;
  imageSource: string;
}

export function RemoveImageBackgroundNotificationTag({...props}: RemoveImageBackgroundNotificationTagProps): ReactElement<any> | null {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const backgroundRemovalCreditsCost = useGetRemoveImageBackgroundCreditsCost(props.imageSource, props.hashedFilename);
  const backgroundRemovalCredits = useGetTeamCredits() ?? 0;
  const areCreditsSufficient = backgroundRemovalCredits >= (backgroundRemovalCreditsCost ?? 1);
  const isPremiumUser = isUserPremium();

  if (backgroundRemovalCreditsCost === 0) {
    return null;
  }

  if (isPremiumUser && areCreditsSufficient) {
    return null;
  }

  const getRoundedBgCredits = (): string => {
    if (backgroundRemovalCredits === null) {
      return '...';
    }

    return formatToNearestThousand(backgroundRemovalCredits);
  };

  const getFormattedCredits = (): string => {
    if (backgroundRemovalCredits === null) {
      return '...';
    }

    return formatNumber(backgroundRemovalCredits);
  };

  if (!isPremiumUser) {
    return (
      <div className={styles.container}>
        <Icon icon="icon-crown" size={IconSize.SIZE_ICON_20} className="color-premium" />
      </div>
    );
  }

  const getNotificationBackgroundStyle = (): string => {
    if (areCreditsSufficient) {
      if (showTooltip) {
        return styles.enabledHoveredNotification;
      }
      return styles.enabledNotification;
    }

    if (showTooltip) {
      return styles.disbledHoveredNotification;
    }
    return styles.disabledNotification;
  };

  const getTooltipContent = (): ReactElement<any> => {
    const costStr =
      backgroundRemovalCreditsCost !== null && backgroundRemovalCreditsCost > 1
        ? window.i18next.t('pmwjs_will_cost_x_credit', {creditsCost: backgroundRemovalCreditsCost})
        : window.i18next.t('pmwjs_will_cost_1_credit');
    return (
      <div className="flex-center flex-v-row">
        <Text type={TextType.BODY} size={TextSize.XSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: getFormattedCredits()})} />
        <Text dangerouslySetInnerHTML type={TextType.BODY} size={TextSize.XSMALL} val={costStr} />
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <Notification
        isRound={false}
        className={`${styles.notification} hover-transition-all ${getNotificationBackgroundStyle()}`}
        showLoading={backgroundRemovalCredits === null}
        text={getRoundedBgCredits()}
        onMouseEnter={(): void => {
          if (!showTooltip) {
            setShowTooltip(true);
          }
        }}
        onMouseLeave={(): void => {
          if (showTooltip) {
            setShowTooltip(false);
          }
        }}
      />
      {backgroundRemovalCredits !== null && showTooltip ? (
        <Tooltip className={styles.tooltip} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} id="outline-remove-image-bg-credits-tooltip" text={getTooltipContent()} />
      ) : null}
    </div>
  );
}
